import { computeMenuPosition, wasClickOutside } from "@livekit/components-core";
import { MediaDeviceSelect } from "@livekit/components-react";
import * as React from "react";

/**
 * Mic chevron menu with both microphone and speaker device lists.
 */
export function LiveInterviewAudioDeviceMenu({
  onMicrophoneDeviceChange,
  onSpeakerDeviceChange,
  disabled = false,
  ...props
}) {
  const [isOpen, setIsOpen] = React.useState(false);
  const [devices, setDevices] = React.useState([]);
  const [updateRequired, setUpdateRequired] = React.useState(true);
  const [needPermissions, setNeedPermissions] = React.useState(false);

  const buttonRef = React.useRef(null);
  const menuRef = React.useRef(null);

  React.useLayoutEffect(() => {
    if (isOpen) {
      setNeedPermissions(true);
    }
  }, [isOpen]);

  React.useLayoutEffect(() => {
    let cleanup;
    if (buttonRef.current && menuRef.current && (devices.length || updateRequired)) {
      cleanup = computeMenuPosition(buttonRef.current, menuRef.current, (x, y) => {
        if (menuRef.current) {
          Object.assign(menuRef.current.style, { left: `${x}px`, top: `${y}px` });
        }
      });
    }
    setUpdateRequired(false);
    return () => {
      cleanup?.();
    };
  }, [devices, updateRequired]);

  const handleClickOutside = React.useCallback(
    (event) => {
      if (!menuRef.current || event.target === buttonRef.current) return;
      if (isOpen && wasClickOutside(menuRef.current, event)) {
        setIsOpen(false);
      }
    },
    [isOpen]
  );

  React.useEffect(() => {
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, [handleClickOutside]);

  const handleDeviceChange = (kind, deviceId) => {
    setIsOpen(false);
    if (kind === "audioinput") {
      onMicrophoneDeviceChange?.(deviceId);
      return;
    }
    onSpeakerDeviceChange?.(deviceId);
  };

  return (
    <>
      <button
        type="button"
        className="lk-button lk-button-menu"
        aria-pressed={isOpen}
        aria-label="Audio devices"
        title="Audio devices"
        disabled={disabled}
        {...props}
        onClick={() => setIsOpen((open) => !open)}
        ref={buttonRef}
      />
      {!disabled ? (
        <div
          className="lk-device-menu li-audio-device-menu"
          ref={menuRef}
          style={{ visibility: isOpen ? "visible" : "hidden" }}
        >
          <div className="lk-device-menu-heading">Microphone</div>
          <MediaDeviceSelect
            kind="audioinput"
            requestPermissions={needPermissions}
            onDeviceListChange={setDevices}
            onActiveDeviceChange={(deviceId) => handleDeviceChange("audioinput", deviceId)}
          />
          <div className="lk-device-menu-heading">Speaker</div>
          <MediaDeviceSelect
            kind="audiooutput"
            requestPermissions={needPermissions}
            onDeviceListChange={setDevices}
            onActiveDeviceChange={(deviceId) => handleDeviceChange("audiooutput", deviceId)}
          />
        </div>
      ) : null}
    </>
  );
}
